import React from "react";
import { Link } from "react-router-dom";
import type { AotdDay } from "../printsApi";
import { authorLabel } from "../dayFormat";
import { preorderErrorText, usePreorders } from "../usePreorders";
import { useGathering } from "../useGathering";
import { FitFace } from "./FitFace";
import { ORIGINAL_FORMATS } from "./useToday";

// The day's originals (1 of 1, holographic foil) on show to everyone, like pieces behind glass
// with a small label. Only the day's author gets a claim button. The original trading card is
// gathered like any card (it counts toward the 21 of a set); the original wall card is a pre-order.

export const OriginalPiece: React.FC<{ day: AotdDay }> = ({ day }) => {
  const pre = usePreorders();
  const gathering = useGathering();
  const author = authorLabel(day);
  const isCard = (format: string) => format === "trading-card";

  return (
    <section className="tp-og" aria-labelledby="tp-og-title">
      <div className="tp-og-head">
        <h2 id="tp-og-title" className="tp-h2">
          original · 1 of 1
        </h2>
        <p className="tp-note">
          {day.isAuthor
            ? "this day is yours, so its originals are yours to claim. one of each, with holographic foil."
            : `one of each, with holographic foil. on show here; only ${author} can claim them.`}
        </p>
      </div>
      <ul className="tp-og-row">
        {ORIGINAL_FORMATS.map((info) => {
          const state = day.originals[info.format] ?? "free";
          const mine = isCard(info.format) ? gathering.held(day.number, "original") : pre.held(day.number, info.format, "original");
          const letGo = isCard(info.format) ? gathering.locked(day.number, "original") : pre.locked(day.number, info.format, "original");
          const busy = !pre.ready || !gathering.ready || pre.busy || gathering.busy;
          const claim = () =>
            isCard(info.format) ? gathering.gather(day.number, "original") : pre.setQuantity(day.number, info.format, "original", 1);
          const release = () => {
            if (!mine) return;
            if (isCard(info.format)) gathering.putBack(mine);
            else pre.setQuantity(day.number, info.format, "original", 0);
          };
          return (
            <li key={info.format} className="tp-og-item">
              <div className="tp-og-glass">
                <FitFace day={day} format={info.format} edition="original" fill={0.72} />
                <span className="tp-og-sheen" aria-hidden="true" />
              </div>
              <div className="tp-og-label">
                <b>original {info.name}</b>
                <span>{info.size}</span>
                {day.isAuthor && pre.signedIn ? (
                  mine || state === "mine" ? (
                    <span className="tp-og-state">
                      yours{isCard(info.format) ? " · gathered" : ""}
                      {mine && !mine.setId && (
                        <>
                          {" · "}
                          <button type="button" className="tp-link-btn" disabled={busy} onClick={release}>
                            let it go
                          </button>
                        </>
                      )}
                    </span>
                  ) : letGo ? (
                    <span className="tp-og-state">you let it go</span>
                  ) : state === "taken" ? (
                    <span className="tp-og-state">already claimed</span>
                  ) : (
                    <button type="button" className="tp-btn tp-btn-holo" disabled={busy} onClick={claim}>
                      claim it
                    </button>
                  )
                ) : (
                  <span className="tp-og-state">{state === "free" ? "not for sale" : `claimed by ${author}`}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {day.isAuthor && !pre.signedIn && (
        <Link to="/login" className="tp-link">
          sign in to claim
        </Link>
      )}
      {pre.error || gathering.error ? <p className="tp-error">{preorderErrorText(pre.error || gathering.error)}</p> : null}
    </section>
  );
};

export default OriginalPiece;
